import homeBg from "../../assets/images/home-bg.svg";
import aiMeasureLogo from '../../assets/images/logos/ai-measure-logo-black.svg'
import toyoshimaLogo from '../../assets/images/logos/toyoshima-black.svg'
import openEyeIcon from '../../assets/images/icons/eye-open.svg'
import closeEyeIcon from '../../assets/images/icons/eye-close.svg'
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PATH_INFO } from "../utils/helper";
import { UpdateAuthContext } from "../../context/authContext";
import { isValidEmail } from "../../utils/validation";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";

export function Login() {
  const { login } = useContext(UpdateAuthContext)
  const { t, i18n } = useTranslation()

  const navigate = useNavigate()

  const [userInfo, setUserInfo] = useState({ email: '', password: '' })
  const [isShow, setIsShow] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    setErrorMessage('')
  }, [i18n.language])

  const onToggleShow = useCallback(() => {
    setIsShow((prev) => !prev)
  }, [])

  const onSubmitLogin = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedEmail = userInfo.email.trim()
    const loginPayload = { ...userInfo, email: trimmedEmail }
    if (!trimmedEmail) {
      setErrorMessage(t("auth.emailRequired"))
      return
    }
    if (!isValidEmail(trimmedEmail)) {
      setErrorMessage(t("auth.emailInvalid"))
      return
    }
    if (!userInfo.password.trim()) {
      setErrorMessage(t("auth.passwordRequired"))
      return
    }

    try {
      const result = await login(loginPayload)
      if (result.ok) {
        navigate(PATH_INFO.HOME.PATH)
      } else {
        setErrorMessage(result.errorMessage || t("auth.loginFailed"))
      }
    } catch {
      setErrorMessage(t("auth.loginFailed"))
    }
  }, [navigate, login, t, userInfo])

  return (
    <div
      className="flex items-center justify-center min-h-[100dvh] px-4 py-6 sm:px-6 bg-cover bg-center"
      style={{ backgroundImage: `url(${homeBg})` }}
    >
      <div className="flex w-full max-w-6xl min-w-[320px] h-auto min-h-0 rounded-[10px] overflow-hidden flex-col tablet-lg:flex-row tablet-lg:h-[914px] tablet-lg:min-h-[600px] tablet-lg:max-h-[calc(100vh-3rem)]">
        <div className="flex flex-col justify-between items-center p-6 sm:p-10 tablet-lg:p-12 bg-[#F3F3F3] w-full tablet-lg:w-1/2 gap-6 text-center">
          <p className="font-hiragino text-lg sm:text-xl tablet-lg:text-[24px]">WELCOME AI MEASURE!</p>
          <img src={aiMeasureLogo} alt="logo" className="w-40 sm:w-44 tablet-lg:w-[217px]" />
          <img src={toyoshimaLogo} alt="logo" className="w-28 sm:w-32 tablet-lg:w-[151px] max-tablet-lg:hidden" />
        </div>
        <div className="relative flex flex-col items-center justify-center bg-white w-full tablet-lg:w-1/2 font-[Poppins] px-6 sm:px-10 tablet-lg:px-12 py-10 tablet-lg:py-16 pb-20 tablet-lg:pb-24 h-full">
          <div className="absolute right-4 top-4">
            <LanguageSwitcher />
          </div>
          <p className="font-bold text-lg sm:text-xl tablet-lg:text-[20px] mb-6 tablet-lg:mb-8">{t("auth.loginTitle")}</p>
          <form className="flex flex-col w-full max-w-md" onSubmit={onSubmitLogin}>
            <div className={`flex flex-col mb-[20px] border-b-[1px] ${errorMessage ? 'border-red-600' : 'border-black'}`}>
              <label className="text-[16px]">{t("auth.email")}(*)</label>
              <input
                type="email"
                className="pl-[10px] py-[7px] outline-none font-normal"
                aria-describedby="login-error-message"
                aria-invalid={Boolean(errorMessage)}
                value={userInfo.email}
                onChange={(e) => {
                  setUserInfo((prev) => ({ ...prev, email: e.target.value }))
                  setErrorMessage('')
                }}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-[16px]">{t("auth.password")}</label>
              <div className={`relative w-full border-b-[1px] ${errorMessage ? 'border-red-600' : 'border-black'}`}>
                <input
                  type={isShow ? 'text' : 'password'}
                  className='pl-[10px] py-[7px] outline-none w-full'
                  aria-describedby="login-error-message"
                  aria-invalid={Boolean(errorMessage)}
                  value={userInfo.password}
                  onChange={(e) => {
                    setUserInfo((prev) => ({ ...prev, password: e.target.value }))
                    setErrorMessage('')
                  }}
                />
                <div onClick={onToggleShow} className="absolute right-[12px] bottom-1/2 translate-y-1/2 hover:cursor-pointer">
                  {isShow ? (
                    <img src={openEyeIcon} alt="open" className="w-[22px] h-[17px]" />
                  ) : (
                    <img src={closeEyeIcon} alt="close" className="w-6 h-[21px]" />
                  )}
                </div>
              </div>
              <p
                className={`text-sm text-red-600 mt-2 min-h-[20px] ${errorMessage ? 'visible' : 'invisible'}`}
                role="alert"
                id="login-error-message"
              >
                {errorMessage || ''}
              </p>
            </div>
            <button
              className="flex items-center justify-center w-full max-w-md h-14 sm:h-16 tablet-lg:h-[73px] bg-black text-white rounded-[10px] font-bold mt-10 tablet-lg:mt-16"
              type="submit"
            >{t("auth.loginNow")}</button>
          </form>
          <div className="flex justify-center mt-8 tablet-lg:mt-0 tablet-lg:absolute tablet-lg:bottom-[50px] w-full max-w-xs h-[31px]">
            <button className="text-[14px] font-medium w-fit underline">{t("auth.forgotPassword")}</button>
          </div>
        </div>
        <div className="bg-[#F3F3F3] h-28 flex items-center justify-center tablet-lg:hidden">
          <img src={toyoshimaLogo} alt="logo" className="w-28 sm:w-32 tablet-lg:w-[151px]" />
        </div>
      </div>
    </div>
  );
};
